

import { makeStyles } from 'tss-react/mui';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ErrorInfo,
  HtmlTrans,
  Typography,
  useSnackbarHandler,
} from 'web-library';
import { usePostDeleteValidCeqTestDates } from '../../../../api-refactored/exam-configuration/usePostDeleteValidCeqTestDates';
import { ValidCeqTestDateModelResponse } from '../../../../api-refactored/exam-configuration/useGetValidCeqTestDates';

const useStyles = makeStyles()((theme) =>
({
  actionContainer: {
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
}));

interface RemoveModalProps {
  year: number;
  isModalOpened: boolean;
  setModalOpened: Dispatch<SetStateAction<boolean>>;
  fetchValidCeqTestDateListing: () => void;
  isStrictlyAeisExam: boolean;
  defaultData?: ValidCeqTestDateModelResponse;
  registrationExamLevelUuid: string;
}

const ValidCeqTestDateRemoveModal = ({
  year,
  isModalOpened,
  setModalOpened,
  fetchValidCeqTestDateListing,
  isStrictlyAeisExam,
  defaultData,
  registrationExamLevelUuid,
}: RemoveModalProps) => {
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();
  const { classes } = useStyles();
  const [apiErrorMsg, setApiErrorMsg] = useState<string | null>(null);

  const {
    mutate: deleteValidCeqTestDatesMutate,
    status: deleteValidCeqTestDatesStatus,
    reset: deleteValidCeqTestDatesReset,
  } = usePostDeleteValidCeqTestDates({
    onSuccess: () => {
      fetchValidCeqTestDateListing();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: (
          <HtmlTrans
            i18nKey="exam->>ValidCeqTestDateConfiguration.ValidCeqTestDateDeleted"
            values={{
              examLevelType: isStrictlyAeisExam ? 'AEIS' : 'S-AEIS',
              admissionYear: year,
            }}
          />
        ),
      });
      onDialogCloseHandler();
    },
    onError: () => {
      setApiErrorMsg(t('components->>Error.DefaultErrorMsg'));
    },
  });

  const onDeleteHandler = () => {
    setApiErrorMsg(null);
    deleteValidCeqTestDatesMutate({
      validCeqTestDateUuid: defaultData?.uuid || '',
      registrationExamLevelUuid,
    });
  };

  const onDialogCloseHandler = () => {
    deleteValidCeqTestDatesReset();
    setApiErrorMsg(null);
    setModalOpened(false);
  };

  return (
    <Dialog id="remove-valid-ceq-test-dates-modal" open={isModalOpened} maxWidth="md">
      <DialogTitle>{t('exam->>ValidCeqTestDateConfiguration.RemoveTitle')}</DialogTitle>

      <DialogContent>
        <Typography variant="body1">
          <HtmlTrans
            i18nKey="exam->>ValidCeqTestDateConfiguration.RemoveContentValidCeqTestDate"
            values={{
              examLevelType: isStrictlyAeisExam ? 'AEIS' : 'S-AEIS',
              admissionYear: year,
            }}
          />
        </Typography>

        {apiErrorMsg && <ErrorInfo label={apiErrorMsg} />}
      </DialogContent>
      <DialogActions>
        <Box className={classes.actionContainer}>
          <Button
            id="button-cancel-remove-valid-ceq-test-dates"
            type="button"
            variant="text"
            onClick={onDialogCloseHandler}
          >
            {t('exam->>ExamConfiguration.Cancel')}
          </Button>
          <ApiButton
            id="button-proceed-remove-valid-ceq-test-dates"
            status={deleteValidCeqTestDatesStatus}
            onClick={onDeleteHandler}
          >
            {t('exam->>ExamConfiguration.Proceed')}
          </ApiButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ValidCeqTestDateRemoveModal;
